import { z } from "zod";
import { isoDate, isoDateTime, moneyMinor, optionalEmail, optionalText, phoneInput, text, uuid } from "./common";

export const PAYER_KINDS = ["insurer", "tpa", "government_scheme", "corporate", "other"] as const;
export const PAYER_KIND_LABELS: Record<(typeof PAYER_KINDS)[number], string> = {
  insurer: "Insurer", tpa: "TPA", government_scheme: "Government scheme", corporate: "Corporate", other: "Other",
};

export const insuranceProviderInput = z.object({
  name: text(160),
  kind: z.enum(PAYER_KINDS),
  payerCode: optionalText(60),
  contactName: optionalText(120),
  phone: phoneInput,
  email: optionalEmail,
  portalUrl: z.string().trim().url().regex(/^https?:\/\//).max(300).nullish().or(z.literal("")).transform((v) => v || null),
  address: optionalText(500),
  notes: optionalText(2000),
  isActive: z.boolean().default(true),
});
export const updateInsuranceProviderInput = insuranceProviderInput.extend({ version: z.number().int().positive() });

export interface InsuranceProvider {
  id: string;
  name: string;
  kind: (typeof PAYER_KINDS)[number];
  payerCode: string | null;
  contactName: string | null;
  phone: string | null;
  email: string | null;
  portalUrl: string | null;
  address: string | null;
  notes: string | null;
  isActive: boolean;
  openClaims: number;
  version: number;
}

export const HOLDER_RELATIONSHIPS = ["self", "spouse", "child", "parent", "employer", "other"] as const;

export const insurancePolicyInput = z
  .object({
    providerId: uuid,
    tpaId: uuid.nullish().transform((v) => v ?? null),
    policyNumber: text(80),
    memberId: optionalText(80),
    holderName: text(160),
    holderRelationship: z.enum(HOLDER_RELATIONSHIPS).default("self"),
    coverageStart: isoDate.nullish().transform((v) => v ?? null),
    coverageEnd: isoDate.nullish().transform((v) => v ?? null),
    sumInsuredMinor: moneyMinor.nullish().transform((v) => v ?? null),
    coverageNotes: optionalText(2000),
    isPrimary: z.boolean().default(true),
  })
  .refine((p) => !p.coverageStart || !p.coverageEnd || p.coverageEnd >= p.coverageStart, { path: ["coverageEnd"], message: "Ends before it starts" })
  .refine((p) => p.tpaId !== p.providerId, { path: ["tpaId"], message: "The TPA must differ from the insurer" });
export type InsurancePolicyInput = z.input<typeof insurancePolicyInput>;
export const updateInsurancePolicyInput = z.object({ version: z.number().int().positive(), policy: insurancePolicyInput });

export interface InsurancePolicy {
  id: string;
  patientId: string;
  provider: { id: string; name: string; kind: string };
  tpa: { id: string; name: string } | null;
  policyNumber: string;
  memberId: string | null;
  holderName: string;
  holderRelationship: (typeof HOLDER_RELATIONSHIPS)[number];
  coverageStart: string | null;
  coverageEnd: string | null;
  sumInsuredMinor: number | null;
  currency: string | null;
  coverageNotes: string | null;
  isPrimary: boolean;
  /** Coverage period includes today. */
  active: boolean;
  version: number;
}

export const CLAIM_STATUSES = ["draft", "submitted", "in_review", "approved", "partially_approved", "rejected", "settled", "cancelled"] as const;
export type ClaimStatus = (typeof CLAIM_STATUSES)[number];
export const CLAIM_STATUS_LABELS: Record<ClaimStatus, string> = {
  draft: "Draft", submitted: "Submitted", in_review: "Under review", approved: "Approved", partially_approved: "Partly approved",
  rejected: "Rejected", settled: "Settled", cancelled: "Cancelled",
};

const claimLine = z.object({
  invoiceItemId: uuid.nullish().transform((v) => v ?? null),
  serviceId: uuid.nullish().transform((v) => v ?? null),
  description: text(300),
  claimedMinor: moneyMinor,
});

export const createClaimInput = z
  .object({
    policyId: uuid,
    claimType: z.enum(["pre_authorization", "claim"]).default("claim"),
    /** Required for a claim; lines default to the invoice's lines (with their totals). */
    invoiceId: uuid.nullish().transform((v) => v ?? null),
    branchId: uuid.nullish().transform((v) => v ?? null),
    lines: z.array(claimLine).max(100).optional(),
    notes: optionalText(2000),
  })
  .refine((c) => c.claimType === "pre_authorization" || !!c.invoiceId, { path: ["invoiceId"], message: "A claim is made against an invoice" });
export type CreateClaimInput = z.input<typeof createClaimInput>;

export const updateClaimInput = z.object({
  version: z.number().int().positive(),
  lines: z.array(claimLine).min(1).max(100),
  notes: optionalText(2000),
});

export const claimDecisionInput = z
  .object({
    version: z.number().int().positive(),
    decision: z.enum(["in_review", "approved", "rejected"]),
    payerClaimReference: optionalText(80),
    /** Approved amount per line (by line id); omitted lines are approved in full when the decision is "approved". */
    lines: z.array(z.object({ id: uuid, approvedMinor: moneyMinor, remarks: optionalText(500) })).max(100).default([]),
    rejectionReason: optionalText(1000),
  })
  .refine((d) => d.decision !== "rejected" || !!d.rejectionReason, { path: ["rejectionReason"], message: "Give the payer's reason" });

export const settleClaimInput = z.object({
  version: z.number().int().positive(),
  amountMinor: moneyMinor.positive(),
  receivedAt: isoDateTime.nullish().transform((v) => v ?? null),
  reference: optionalText(80),
});

export const claimListQuery = z.object({
  status: z.enum([...CLAIM_STATUSES, "open", "all"]).default("open"),
  providerId: uuid.optional(),
  patientId: uuid.optional(),
  limit: z.coerce.number().int().min(1).max(200).default(100),
});

export interface ClaimLine {
  id: string;
  lineNo: number;
  invoiceItemId: string | null;
  serviceId: string | null;
  description: string;
  claimedMinor: number;
  approvedMinor: number | null;
  remarks: string | null;
}

export interface InsuranceClaim {
  id: string;
  number: string;
  claimType: "pre_authorization" | "claim";
  status: ClaimStatus;
  patient: { id: string; fullName: string; patientCode: string };
  policy: { id: string; policyNumber: string; memberId: string | null };
  payer: { id: string; name: string };
  tpa: { id: string; name: string } | null;
  invoice: { id: string; number: string | null; balanceMinor: number } | null;
  branch: { id: string; name: string };
  currency: string;
  claimedMinor: number;
  approvedMinor: number | null;
  settledMinor: number | null;
  /** What the patient still owes on the invoice if the claim pays as approved. */
  patientShareMinor: number | null;
  payerClaimReference: string | null;
  notes: string | null;
  rejectionReason: string | null;
  submittedAt: string | null;
  decidedAt: string | null;
  settledAt: string | null;
  settlementPayment: { id: string; number: string } | null;
  createdAt: string;
  version: number;
  lines?: ClaimLine[];
  events?: { toStatus: string; fromStatus: string | null; note: string | null; actor: string | null; occurredAt: string }[];
  documents?: { id: string; documentId: string; kind: string; title: string }[];
}

export const CLAIM_DOCUMENT_KINDS = ["claim_form", "pre_auth_form", "invoice", "prescription", "xray", "clinical_notes", "id_proof", "payer_letter", "other"] as const;
export const attachClaimDocumentInput = z.object({ documentId: uuid, kind: z.enum(CLAIM_DOCUMENT_KINDS) });
